import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  UserIcon,
  CogIcon,
  ShoppingBagIcon,
  HeartIcon,
  MapPinIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
  PencilIcon,
  EyeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const ProfilePage = () => {
  const { user, profile, resendEmailConfirmation, updateProfile, supabase } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [emailResending, setEmailResending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
    gender: 'male'
  });

  // Real data states
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    title: '',
    full_name: '',
    phone: '',
    address_line: '',
    city: '',
    district: '',
    postal_code: '',
    is_default: false
  });
  const [newPayment, setNewPayment] = useState({
    type: 'credit_card',
    card_holder_name: '',
    card_last_four: '',
    card_brand: 'visa',
    expiry_month: '',
    expiry_year: '',
    is_default: false
  });

  // Kullanıcı ve profil verisi değiştiğinde state'i güncelle
  useEffect(() => {
    if (user) {
      const fullName = profile?.full_name || '';
      const nameParts = fullName.split(' ');
      
      setProfileData({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user?.email || '',
        phone: profile?.phone || '',
        birthDate: profile?.birth_date || '',
        gender: profile?.gender || 'male'
      });
    }
  }, [user, profile]);

  // Tab değiştiğinde gerekli data'yı fetch et
  useEffect(() => {
    if (!user) return;

    const fetchTabData = async () => {
      setLoading(true);
      
      try {
        switch (activeTab) {
          case 'orders':
            await fetchOrders();
            break;
          case 'favorites':
            await fetchFavorites();
            break;
          case 'addresses':
            await fetchAddresses();
            break;
          case 'payments':
            await fetchPaymentMethods();
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Tab data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTabData();
  }, [activeTab, user]);

  // Add new address
  const addAddress = async () => {
    if (!user || !newAddress.title || !newAddress.address_line || !newAddress.city) {
      alert('Lütfen zorunlu alanları doldurun.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_addresses')
        .insert({
          ...newAddress,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      setAddresses([data, ...addresses]);
      setShowAddressModal(false);
      setNewAddress({
        title: '',
        full_name: '',
        phone: '',
        address_line: '',
        city: '',
        district: '',
        postal_code: '',
        is_default: false
      });
      alert('Adres başarıyla eklendi!');
    } catch (error) {
      console.error('Address add error:', error);
      alert('Adres eklenirken bir hata oluştu: ' + error.message);
    }
  };

  // Add new payment method
  const addPaymentMethod = async () => {
    if (!user || !newPayment.card_holder_name || !newPayment.card_last_four || !newPayment.expiry_month || !newPayment.expiry_year) {
      alert('Lütfen zorunlu alanları doldurun.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_payment_methods')
        .insert({
          ...newPayment,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      setPaymentMethods([data, ...paymentMethods]);
      setShowPaymentModal(false);
      setNewPayment({
        type: 'credit_card',
        card_holder_name: '',
        card_last_four: '',
        card_brand: 'visa',
        expiry_month: '',
        expiry_year: '',
        is_default: false
      });
      alert('Ödeme yöntemi başarıyla eklendi!');
    } catch (error) {
      console.error('Payment method add error:', error);
      alert('Ödeme yöntemi eklenirken bir hata oluştu: ' + error.message);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profil Bilgileri', icon: UserIcon },
    { id: 'orders', name: 'Siparişlerim', icon: ShoppingBagIcon },
    { id: 'favorites', name: 'Favorilerim', icon: HeartIcon },
    { id: 'addresses', name: 'Adreslerim', icon: MapPinIcon },
    { id: 'payments', name: 'Ödeme Yöntemleri', icon: CreditCardIcon },
    { id: 'notifications', name: 'Bildirimler', icon: BellIcon },
    { id: 'security', name: 'Güvenlik', icon: ShieldCheckIcon },
  ];

  // Data fetching functions
  const fetchOrders = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(
            id,
            quantity,
            price,
            products(name, image_url)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setOrders(data || []);
    } catch (error) {
      console.error('Orders fetch error:', error);
      setOrders([]);
    }
  };

  const fetchFavorites = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('favorites')
        .select(`
          *,
          products(
            id,
            name,
            price,
            image_url,
            category
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setFavorites(data || []);
    } catch (error) {
      console.error('Favorites fetch error:', error);
      setFavorites([]);
    }
  };

  const fetchAddresses = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_addresses')
        .select('*')
        .eq('user_id', user.id)
        .order('is_default', { ascending: false });
      
      if (error) throw error;
      
      setAddresses(data || []);
    } catch (error) {
      console.error('Addresses fetch error:', error);
      setAddresses([]);
    }
  };

  const fetchPaymentMethods = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .order('is_default', { ascending: false });
      
      if (error) throw error;
      
      setPaymentMethods(data || []);
    } catch (error) {
      console.error('Payment methods fetch error:', error);
      setPaymentMethods([]);
    }
  };

  const handleInputChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const saveProfile = async () => {
    console.log('Save profile started...');
    setSaving(true);
    
    try {
      // User kontrolü
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      // Profil verilerini birleştir
      const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
      
      // Güncelleme verisini hazırla
      const updates = {
        full_name: fullName
      };

      // Boş olmayan alanları ekle
      if (profileData.phone && profileData.phone.trim()) {
        updates.phone = profileData.phone.trim();
      }
      
      if (profileData.birthDate) {
        updates.birth_date = profileData.birthDate;
      }
      
      if (profileData.gender) {
        updates.gender = profileData.gender;
      }

      console.log('Sending updates:', updates);
      console.log('User ID:', user.id);

      const result = await updateProfile(updates);
      console.log('Update result:', result);
      
      if (result.error) {
        console.error('Update error:', result.error);
        throw result.error;
      }

      // Başarılı kayıt
      console.log('Profile updated successfully!');
      
      // Toast notification yerine console ve UI feedback
      setEditMode(false);
      
      // Success feedback with setTimeout to ensure UI updates
      setTimeout(() => {
        alert('Profil bilgileriniz başarıyla güncellendi!');
      }, 100);
      
    } catch (error) {
      console.error('Profile update error:', error);
      
      // Error feedback
      let errorMessage = 'Profil güncellenirken bir hata oluştu.';
      
      if (error.message) {
        errorMessage += ` Hata: ${error.message}`;
      }
      
      if (error.code) {
        errorMessage += ` (Kod: ${error.code})`;
      }
      
      alert(errorMessage);
    } finally {
      console.log('Save profile finished');
      setSaving(false);
    }
  };

  const handleResendEmail = async () => {
    setEmailResending(true);
    try {
      const { data, error } = await resendEmailConfirmation();
      if (error) throw error;
      
      alert('Doğrulama e-postası gönderildi! Lütfen e-posta kutunuzu kontrol edin.');
    } catch (error) {
      alert('E-posta gönderilirken bir hata oluştu: ' + error.message);
    } finally {
      setEmailResending(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            {/* E-posta Doğrulama Uyarısı */}
            {user && !user.email_confirmed_at && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-red-800 font-medium mb-1">
                      E-posta Adresinizi Doğrulayın
                    </h4>
                    <p className="text-red-700 text-sm mb-3">
                      Hesabınızı güvende tutmak için e-posta adresinizi doğrulamanız gerekiyor.
                      <br />
                      <strong>{user.email}</strong> adresine doğrulama e-postası gönderin.
                    </p>
                    <button
                      onClick={handleResendEmail}
                      disabled={emailResending}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                      <span>{emailResending ? 'Gönderiliyor...' : 'Doğrulama E-postası Gönder'}</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Profil Bilgileri</h3>
              <button
                onClick={() => setEditMode(!editMode)}
                className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-700 transition-colors duration-300"
              >
                <PencilIcon className="h-4 w-4" />
                <span>{editMode ? 'İptal' : 'Düzenle'}</span>
              </button>
            </div>

            <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Soyad
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Doğum Tarihi
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={profileData.birthDate}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cinsiyet
                </label>
                <select
                  name="gender"
                  value={profileData.gender}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-50 transition-colors duration-300"
                >
                  <option value="male">Erkek</option>
                  <option value="female">Kadın</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
            </form>

            {editMode && (
              <div className="flex space-x-4">
                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-300"
                >
                  İptal
                </button>
              </div>
            )}
          </div>
        );

      case 'orders':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Siparişlerim</h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                <span className="ml-2 text-gray-600">Siparişler yükleniyor...</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Henüz siparişiniz bulunmuyor.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const statusColors = {
                    pending: 'text-amber-600',
                    confirmed: 'text-blue-600',
                    preparing: 'text-orange-600',
                    shipped: 'text-purple-600',
                    delivered: 'text-emerald-600',
                    cancelled: 'text-red-600'
                  };
                  
                  const statusLabels = {
                    pending: 'Beklemede',
                    confirmed: 'Onaylandı',
                    preparing: 'Hazırlanıyor',
                    shipped: 'Kargoda',
                    delivered: 'Teslim Edildi',
                    cancelled: 'İptal Edildi'
                  };
                  
                  return (
                    <div key={order.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div>
                            <p className="font-semibold text-gray-900">#{order.order_number || order.id.slice(0, 8)}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(order.created_at).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]} bg-current bg-opacity-10`}>
                          {statusLabels[order.status] || order.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm text-gray-600">
                            {order.order_items?.length || 0} ürün
                          </span>
                          <span className="text-lg font-semibold text-gray-900">
                            ₺{Number(order.total_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <button className="flex items-center space-x-2 text-slate-600 hover:text-slate-700">
                          <EyeIcon className="h-4 w-4" />
                          <span>Detaylar</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'addresses':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Adreslerim</h3>
              <button 
                onClick={() => setShowAddressModal(true)}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300"
              >
                Yeni Adres Ekle
              </button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                <span className="ml-2 text-gray-600">Adresler yükleniyor...</span>
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Henüz kayıtlı adresiniz bulunmuyor.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {addresses.map((address) => (
                  <div key={address.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">{address.title}</h4>
                      {address.is_default && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-600 text-xs rounded-full">
                          Varsayılan
                        </span>
                      )}
                    </div>
                    {address.full_name && (
                      <p className="text-gray-900 font-medium mb-1">{address.full_name}</p>
                    )}
                    {address.phone && (
                      <p className="text-gray-600 text-sm mb-2">{address.phone}</p>
                    )}
                    <p className="text-gray-600 mb-2">{address.address_line}</p>
                    <p className="text-gray-600 mb-4">
                      {address.district && `${address.district}, `}{address.city}
                      {address.postal_code && ` ${address.postal_code}`}
                    </p>
                    <div className="flex space-x-2">
                      <button className="px-3 py-1 text-sm text-slate-600 border border-slate-600 rounded hover:bg-slate-600 hover:text-white transition-colors duration-300">
                        Düzenle
                      </button>
                      <button className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-600 hover:text-white transition-colors duration-300">
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Ödeme Yöntemleri</h3>
              <button 
                onClick={() => setShowPaymentModal(true)}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300"
              >
                Yeni Kart Ekle
              </button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                <span className="ml-2 text-gray-600">Ödeme yöntemleri yükleniyor...</span>
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Henüz kayıtlı ödeme yönteminiz bulunmuyor.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paymentMethods.map((payment) => {
                  const brandColors = {
                    visa: 'from-blue-600 to-blue-800',
                    mastercard: 'from-red-600 to-orange-600',
                    amex: 'from-green-600 to-teal-600',
                    default: 'from-gray-600 to-gray-800'
                  };
                  
                  return (
                    <div key={payment.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-6 bg-gradient-to-r ${brandColors[payment.card_brand] || brandColors.default} rounded`}></div>
                          <span className="font-semibold text-gray-900 capitalize">
                            {payment.card_brand || 'Kart'}
                          </span>
                        </div>
                        {payment.is_default && (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-600 text-xs rounded-full">
                            Varsayılan
                          </span>
                        )}
                      </div>
                      {payment.card_holder_name && (
                        <p className="text-gray-900 font-medium mb-2">{payment.card_holder_name}</p>
                      )}
                      <p className="text-gray-600 mb-2">**** **** **** {payment.card_last_four}</p>
                      <p className="text-gray-600 mb-4">
                        Son kullanma: {payment.expiry_month}/{payment.expiry_year}
                      </p>
                      <div className="flex space-x-2">
                        <button className="px-3 py-1 text-sm text-slate-600 border border-slate-600 rounded hover:bg-slate-600 hover:text-white transition-colors duration-300">
                          Düzenle
                        </button>
                        <button className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-600 hover:text-white transition-colors duration-300">
                          Sil
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'favorites':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Favorilerim</h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                <span className="ml-2 text-gray-600">Favoriler yükleniyor...</span>
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Henüz favori ürününüz bulunmuyor.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((favorite) => (
                  <div key={favorite.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {favorite.products?.image_url && (
                      <img 
                        src={favorite.products.image_url} 
                        alt={favorite.products.name}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {favorite.products?.name || 'Ürün'}
                      </h4>
                      <p className="text-gray-600 text-sm mb-2">
                        {favorite.products?.category}
                      </p>
                      <p className="text-lg font-semibold text-gray-900 mb-4">
                        ₺{Number(favorite.products?.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </p>
                      <div className="flex space-x-2">
                        <button className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300">
                          Sepete Ekle
                        </button>
                        <button className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors duration-300">
                          <HeartIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-600">Bu bölüm yakında eklenecek...</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-full mb-6">
            <UserIcon className="h-8 w-8 text-slate-600" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-luxury font-bold text-gray-900 mb-4">
            Hesabım
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Profilinizi yönetin, siparişlerinizi takip edin ve tercihlerinizi ayarlayın
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Profile Summary */}
              <div className="text-center mb-8 pb-6 border-b border-gray-200">
                <div className="w-20 h-20 bg-slate-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-semibold text-white">
                    {profileData.firstName[0]}{profileData.lastName[0]}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {profileData.firstName} {profileData.lastName}
                </h3>
                <p className="text-gray-600">{profileData.email}</p>
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-300 ${
                      activeTab === tab.id
                        ? 'bg-slate-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {renderTabContent()}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Yeni Adres Ekle</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adres Başlığı *</label>
                <input
                  type="text"
                  value={newAddress.title}
                  onChange={(e) => setNewAddress({...newAddress, title: e.target.value})}
                  placeholder="Ev Adresi, İş Adresi vb."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad</label>
                <input
                  type="text"
                  value={newAddress.full_name}
                  onChange={(e) => setNewAddress({...newAddress, full_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                <input
                  type="tel"
                  value={newAddress.phone}
                  onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adres *</label>
                <textarea
                  value={newAddress.address_line}
                  onChange={(e) => setNewAddress({...newAddress, address_line: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Şehir *</label>
                  <input
                    type="text"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">İlçe</label>
                  <input
                    type="text"
                    value={newAddress.district}
                    onChange={(e) => setNewAddress({...newAddress, district: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Posta Kodu</label>
                <input
                  type="text"
                  value={newAddress.postal_code}
                  onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="defaultAddress"
                  checked={newAddress.is_default}
                  onChange={(e) => setNewAddress({...newAddress, is_default: e.target.checked})}
                  className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                />
                <label htmlFor="defaultAddress" className="ml-2 text-sm text-gray-700">
                  Varsayılan adres olarak ayarla
                </label>
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={addAddress}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300"
              >
                Kaydet
              </button>
              <button
                onClick={() => setShowAddressModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-300"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Yeni Kart Ekle</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kart Sahibinin Adı *</label>
                <input
                  type="text"
                  value={newPayment.card_holder_name}
                  onChange={(e) => setNewPayment({...newPayment, card_holder_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kart Numarası Son 4 Hane *</label>
                <input
                  type="text"
                  value={newPayment.card_last_four}
                  onChange={(e) => setNewPayment({...newPayment, card_last_four: e.target.value.slice(0, 4)})}
                  maxLength={4}
                  placeholder="1234"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kart Markası</label>
                <select
                  value={newPayment.card_brand}
                  onChange={(e) => setNewPayment({...newPayment, card_brand: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                >
                  <option value="visa">Visa</option>
                  <option value="mastercard">Mastercard</option>
                  <option value="amex">American Express</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ay *</label>
                  <select
                    value={newPayment.expiry_month}
                    onChange={(e) => setNewPayment({...newPayment, expiry_month: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  >
                    <option value="">Seçin</option>
                    {Array.from({length: 12}, (_, i) => (
                      <option key={i+1} value={String(i+1).padStart(2, '0')}>{String(i+1).padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yıl *</label>
                  <select
                    value={newPayment.expiry_year}
                    onChange={(e) => setNewPayment({...newPayment, expiry_year: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  >
                    <option value="">Seçin</option>
                    {Array.from({length: 20}, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return <option key={year} value={year}>{year}</option>
                    })}
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="defaultPayment"
                  checked={newPayment.is_default}
                  onChange={(e) => setNewPayment({...newPayment, is_default: e.target.checked})}
                  className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                />
                <label htmlFor="defaultPayment" className="ml-2 text-sm text-gray-700">
                  Varsayılan ödeme yöntemi olarak ayarla
                </label>
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={addPaymentMethod}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300"
              >
                Kaydet
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-300"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage; 